import { useState } from 'react';
import { ArrowRight, Building2, Check, MapPin, Shield, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Badge, Button, Card, PageHeader } from '../../components/ui';
import { userAPI } from '../../lib/api';
import { PARTY_CHOICES, PRIORITY_CHOICES } from '../../lib/intakeChoices';

const STEPS = ['Location', 'Priorities', 'Affiliation', 'Connect'];

export default function IntakeQuickScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [zip, setZip] = useState('');
  const [priorities, setPriorities] = useState<string[]>([]);
  const [party, setParty] = useState('');
  const [org, setOrg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canAdvance =
    (step === 0 && zip.length >= 5) ||
    (step === 1 && priorities.length > 0) ||
    (step === 2 && party.length > 0) ||
    step === 3;

  async function next() {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      await userAPI.updateProfile({
        zipCode: zip,
        interests: priorities,
        politicalAffiliation: party === 'decline' ? null : party,
      });
      // Resolve districts from the ZIP so the ballot and contact-reps screens
      // work end-to-end. Best-effort — geocoding failures shouldn't block intake.
      try {
        await userAPI.resolveDistricts(zip);
      } catch {
        /* districts can be set later from Profile */
      }
      // Org affiliation has no backend home yet (org matching is a later
      // phase) — keep the answer local so we can sync it once it does.
      if (org) localStorage.setItem('citizen-intake-org', org);
      navigate('/citizen/intake/success');
    } catch {
      toast.error('Could not save your profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col min-h-screen screen-enter">
      <PageHeader
        title="Set up"
        onBack={() => (step === 0 ? navigate('/citizen/intake') : setStep(step - 1))}
        right={
          <button
            onClick={() => navigate('/citizen/home')}
            className="text-xs text-[color:var(--muted-foreground)] px-2 py-1 hover-elevate rounded-md"
          >
            Skip
          </button>
        }
      />

      {/* Progress dots */}
      <div className="px-6 pt-2 pb-1">
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className="flex-1 h-1 rounded-full"
              style={{ background: i <= step ? 'var(--primary)' : 'var(--secondary)' }}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[11px] font-medium">
          <span className="text-[color:var(--muted-foreground)]">
            Step {step + 1} of {STEPS.length}
          </span>
          <span style={{ color: 'var(--primary)' }}>{STEPS[step]}</span>
        </div>
      </div>

      {/* Step body */}
      <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4 space-y-6">
        {step === 0 && <StepLocation zip={zip} setZip={setZip} />}
        {step === 1 && <StepPriorities priorities={priorities} setPriorities={setPriorities} />}
        {step === 2 && <StepParty party={party} setParty={setParty} />}
        {step === 3 && <StepOrg org={org} setOrg={setOrg} />}
      </div>

      {/* Footer */}
      <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <Button size="lg" className="w-full" disabled={!canAdvance || submitting} onClick={next}>
          {step < STEPS.length - 1 ? (
            <>
              Continue <ArrowRight className="w-4 h-4" />
            </>
          ) : (
            <>
              {submitting ? 'Saving…' : 'Finish setup'} <Check className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function StepLocation({ zip, setZip }: { zip: string; setZip: (v: string) => void }) {
  const validZip = zip.length >= 5;
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-extrabold tracking-tight">Where do you vote?</h2>
        <p className="text-[15px] text-[color:var(--muted-foreground)]">
          We use your ZIP to find your districts and the races on your ballot.
        </p>
      </div>

      <div className="space-y-3">
        <label className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)]">
          ZIP code
        </label>
        <input
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
          inputMode="numeric"
          placeholder="80205"
          className="w-full rounded-xl px-4 py-4 text-2xl font-semibold tabular-nums tracking-widest text-center"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        />
      </div>

      {validZip && (
        <Card className="p-4 anim-slide" style={{ background: 'var(--accent)' }}>
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'var(--primary)', color: 'white' }}
            >
              <MapPin className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold">ZIP {zip} saved</p>
              <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">
                We'll match your legislative districts and ballot races to this area.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 flex items-center gap-3" style={{ background: 'transparent', border: '1px dashed var(--border)' }}>
        <Shield className="w-5 h-5 text-[color:var(--muted-foreground)] shrink-0" />
        <p className="text-xs text-[color:var(--muted-foreground)] leading-relaxed">
          Your address is encrypted and never shared without explicit permission. <b>Citizen Standard</b> compliant.
        </p>
      </Card>
    </div>
  );
}

function StepPriorities({
  priorities,
  setPriorities,
}: {
  priorities: string[];
  setPriorities: (v: string[]) => void;
}) {
  const toggle = (p: string) =>
    setPriorities(priorities.includes(p) ? priorities.filter((x) => x !== p) : [...priorities, p]);
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-extrabold tracking-tight">What matters to you?</h2>
        <p className="text-[15px] text-[color:var(--muted-foreground)]">
          We'll highlight issues, hearings, and candidates that touch your priorities.{' '}
          <b className="text-[color:var(--foreground)]">Pick at least one.</b>
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {PRIORITY_CHOICES.map((p) => {
          const on = priorities.includes(p);
          return (
            <button
              key={p}
              onClick={() => toggle(p)}
              className="rounded-full px-4 py-2.5 text-sm font-semibold border hover-elevate active-elevate-2 transition-colors"
              style={
                on
                  ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' }
                  : { background: 'var(--card)', color: 'var(--foreground)', borderColor: 'var(--border)' }
              }
            >
              {on && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
              {p}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 text-xs text-[color:var(--muted-foreground)]">
        <span>{priorities.length} selected</span>
        {priorities.length >= 3 && (
          <Badge variant="gold">
            <Sparkles className="w-3 h-3 mr-1" />
            Great start
          </Badge>
        )}
      </div>
    </div>
  );
}

function StepParty({ party, setParty }: { party: string; setParty: (v: string) => void }) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-extrabold tracking-tight">Party affiliation</h2>
        <p className="text-[15px] text-[color:var(--muted-foreground)]">
          Optional. We use this to flag primary eligibility and balance recommendations.
        </p>
      </div>

      <div className="space-y-2">
        {PARTY_CHOICES.map((opt) => {
          const on = party === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setParty(opt.id)}
              className="w-full rounded-xl px-4 py-3 flex items-center justify-between hover-elevate active-elevate-2 transition-colors border"
              style={
                on
                  ? { background: 'var(--accent)', borderColor: 'var(--primary)' }
                  : { background: 'var(--card)', borderColor: 'var(--border)' }
              }
            >
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{ background: opt.color }} />
                <span className="text-base font-semibold">{opt.label}</span>
              </div>
              <span
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                style={{
                  borderColor: on ? 'var(--primary)' : 'var(--border)',
                  background: on ? 'var(--primary)' : 'transparent',
                }}
              >
                {on && <Check className="w-3 h-3 text-white" />}
              </span>
            </button>
          );
        })}
      </div>

      <button
        onClick={() => setParty('decline')}
        className="text-sm font-medium hover-elevate rounded-md px-2 py-1"
        style={{ color: party === 'decline' ? 'var(--primary)' : 'var(--muted-foreground)' }}
      >
        Prefer not to say
      </button>
    </div>
  );
}

const ORGS = [
  'League of Women Voters CO',
  'Sierra Club Colorado',
  'Colorado Education Assn.',
  'Chamber of Commerce',
  'Not affiliated',
];

function StepOrg({ org, setOrg }: { org: string; setOrg: (v: string) => void }) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-extrabold tracking-tight">Anyone send you here?</h2>
        <p className="text-[15px] text-[color:var(--muted-foreground)]">
          If a civic group invited you, we'll connect to their feed and surveys.
        </p>
      </div>

      <div className="space-y-2">
        {ORGS.map((o) => {
          const on = org === o;
          return (
            <button
              key={o}
              onClick={() => setOrg(on ? '' : o)}
              className="w-full rounded-xl px-4 py-3 flex items-center gap-3 hover-elevate active-elevate-2 transition-colors border"
              style={
                on
                  ? { background: 'var(--accent)', borderColor: 'var(--primary)' }
                  : { background: 'var(--card)', borderColor: 'var(--border)' }
              }
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'var(--muted)', color: 'var(--primary)' }}
              >
                <Building2 className="w-5 h-5" />
              </div>
              <span className="flex-1 text-left text-sm font-semibold">{o}</span>
              {on && <Check className="w-5 h-5" style={{ color: 'var(--primary)' }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
